import { type IVector2, Vector2, Vector3 } from '@seddi/math-v2';

// function triangleArea(a: IVector2, b: IVector2, c: IVector2) {
//   return 0.5 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - c.y));
// }

/**
 * Builds heat deformation values for geometry.
 * @param indices - Geometry indices.
 * @param positions - Geometry 3D positions.
 * @param positions2 - Geometry 2D material coordinates.
 * @returns Heat vertex data.
 */
export function buildHeatMap(
  indices: Uint32Array,
  positions: Float32Array,
  positions2: Float32Array,
): Float32Array {
  const nfaces = indices.length / 3;
  const nvertices = positions.length / 3;

  // According to Typed Array Specification contents are initialized to 0
  const heat = new Float32Array(nvertices);

  const p0 = new Vector3();
  const p1 = new Vector3();
  const p2 = new Vector3();
  const x0 = new Vector2();
  const x1 = new Vector2();
  const x2 = new Vector2();
  const D0 = new Vector3();
  const D1 = new Vector3();
  const D2 = new Vector3();
  const L0 = new Vector2();
  const L1 = new Vector2();
  const L2 = new Vector2();

  for (let i = 0; i < nfaces; ++i) {
    const i0 = indices[i * 3 + 0];
    const i1 = indices[i * 3 + 1];
    const i2 = indices[i * 3 + 2];

    // Face geometry
    p0.set(positions[i0 * 3 + 0], positions[i0 * 3 + 1], positions[i0 * 3 + 2]);
    p1.set(positions[i1 * 3 + 0], positions[i1 * 3 + 1], positions[i1 * 3 + 2]);
    p2.set(positions[i2 * 3 + 0], positions[i2 * 3 + 1], positions[i2 * 3 + 2]);
    x0.set(positions2[i0 * 2 + 0], positions2[i0 * 2 + 1]);
    x1.set(positions2[i1 * 2 + 0], positions2[i1 * 2 + 1]);
    x2.set(positions2[i2 * 2 + 0], positions2[i2 * 2 + 1]);

    // Normalized edge deformation measurement
    D0.set(p1.x - p0.x, p1.y - p0.y, p1.z - p0.z);
    D1.set(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z);
    D2.set(p0.x - p2.x, p0.y - p2.y, p0.z - p2.z);
    L0.set(x1.x - x0.x, x1.y - x0.y);
    L1.set(x2.x - x1.x, x2.y - x1.y);
    L2.set(x0.x - x2.x, x0.y - x2.y);
    const d0 = D0.length();
    const d1 = D1.length();
    const d2 = D2.length();
    const l0 = L0.length();
    const l1 = L1.length();
    const l2 = L2.length();
    const e0 = (d0 - l0) / l0;
    const e1 = (d1 - l1) / l1;
    const e2 = (d2 - l2) / l2;

    // TODO: normalize weights?
    heat[i0] += e0;
    heat[i0] += e2;
    heat[i1] += e1;
    heat[i1] += e0;
    heat[i2] += e2;
    heat[i2] += e1;
  }

  return heat;
}
